import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Room } from '../components/inventory/FloorPlan';
import { defaultRooms } from '../utils/defaultRooms';

interface RoomsContextType {
  rooms: Room[];
  setRooms: (rooms: Room[]) => void;
  refreshRooms: () => void;
}

const RoomsContext = createContext<RoomsContextType | undefined>(undefined);

export const RoomsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [rooms, setRooms] = useState<Room[]>(defaultRooms);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Update rooms whenever defaultRooms changes (HMR)
  useEffect(() => {
    setRooms([...defaultRooms]);
  }, [updateTrigger]);

  const refreshRooms = () => {
    // Force a re-import of defaultRooms
    setRooms([...defaultRooms]);
    setUpdateTrigger(prev => prev + 1);
  };

  // Set up HMR listener
  useEffect(() => {
    if (import.meta.hot) {
      import.meta.hot.on('vite:beforeUpdate', () => {
        console.log('HMR: Refreshing rooms configuration...');
        // Small delay to ensure module is reloaded
        setTimeout(() => {
          setUpdateTrigger(prev => prev + 1);
        }, 100);
      });
    }
  }, []);

  return (
    <RoomsContext.Provider value={{ rooms, setRooms, refreshRooms }}>
      {children}
    </RoomsContext.Provider>
  );
};

export const useRooms = () => {
  const context = useContext(RoomsContext);
  if (context === undefined) {
    throw new Error('useRooms must be used within a RoomsProvider');
  }
  return context;
};
